/// <amd-module name="Repos/Views/PullRequest/Components/PullRequestDetails/PullRequestDetails" />
import * as React from "react";
import { ChangesContext } from "Repos/ChangesCommon/ChangesContext";
import { getGitNavigationService } from "Repos/ChangesCommon/ChangesNavigationService";
import { IChange } from "Repos/ChangesCommon/Contracts";
import { getReposRoutingService } from "Repos/Common/ReposRoutingService";
import { IGitFilesService } from "Repos/Files/GitFilesService";
import { ReposMessages } from "Repos/Messages/Components/ReposMessages";
import * as Resources from "Repos/Views/PullRequest/Resources";
import { ObservableCollection } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";
import { addTabs } from "VSS/Features/PlatformUI/ContributedTab";
import { IPerformanceScenarioPendingWork, PerformanceScenario, PerformanceScenarioContext } from "VSS/Platform/Components/Performance";
import { onClickFPS } from "VSS/Platform/FPS";
import { Components, css, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { getRouteValues } from "VSS/Platform/Navigation";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { IVssContributedTab, TabBar, TabContent } from "VSSUI/Tabs";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";
import { PullRequestMessageType } from "../../Contracts/PullRequest";
import { getPullRequestViewService, IPullRequestViewService } from "../../PullRequestViewService";
import { CommitsTabContent } from "./CommitsTab";
import { FilesTabContent } from "./FilesTab";
import { OverviewTabContent } from "./OverviewTab";
import { PullRequestDetailsHeader } from "./PullRequestDetailsHeader";
import { UpdatesTabContent } from "./UpdatesTab";

interface IPullRequestTab extends IVssContributedTab {
    displaySideBySide?: boolean;
}

function getBuiltInPullRequestTabs(getUrl: (tabName: string) => string): IVssContributedTab[] {
    return [
        {
            id: "overview",
            name: Resources.Overview,
            order: 10,
            render: () => <OverviewTabContent />,
            url: getUrl("overview")
        },
        {
            displaySideBySide: true,
            id: "files",
            name: Resources.Files,
            order: 20,
            render: () => <FilesTabContent />,
            url: getUrl("files")
        } as IPullRequestTab,
        {
            id: "updates",
            name: Resources.Updates,
            order: 30,
            render: () => <UpdatesTabContent />,
            url: getUrl("updates")
        },
        {
            id: "commits",
            name: Resources.Commits,
            order: 40,
            render: () => <CommitsTabContent />,
            url: getUrl("commits")
        }
    ];
}

class PullRequestDetails extends VssComponent {
    private static readonly ignoredTypesSmallScreen = new Set<string | undefined>([
        PullRequestMessageType.reviewersError,
        PullRequestMessageType.newChanges
    ]);

    private timerManagement = new TimerManagement();

    public render() {
        const pageContext = this.context.pageContext;
        const routingService = getReposRoutingService(pageContext);
        let prDetailsService: IPullRequestViewService;

        try {
            prDetailsService = getPullRequestViewService(pageContext);
        } catch (ex) {
            // PR details data does not exist.
            const actionHref = routingService.getPullRequestsUrl();
            const pullRequestId = getRouteValues(pageContext)["parameters"] || "";
            return (
                <ZeroData
                    actionType={ZeroDataActionType.link}
                    actionText={Resources.PullRequestNotFoundAction}
                    actionHref={actionHref}
                    className="flex-grow"
                    imagePath={getAssetLocation(pageContext, "ms.vss-code-web/pr-details-content/Content/invalid-path.svg")}
                    imageAltText={Resources.PullRequestNotFoundPrimary}
                    onActionClick={e => onClickFPS(pageContext, actionHref, true, e)}
                    primaryText={Resources.PullRequestNotFoundPrimary}
                    secondaryText={format(Resources.PullRequestNotFoundSecondary, pullRequestId)}
                />
            );
        }

        const filesService = pageContext.getService<IGitFilesService>("IGitFilesService");
        const navigationService = getGitNavigationService(pageContext);
        const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");

        const tabs = new ObservableCollection<IVssContributedTab>();
        tabs.push(
            getBuiltInPullRequestTabs(tabName =>
                routingService.getPullRequestUrl(
                    prDetailsService.getTargetRepository().name,
                    prDetailsService.getTargetRepository().project.name,
                    prDetailsService.getId(),
                    tabName
                )
            )
        );

        const extensionContext = prDetailsService.getExtensionContext();

        addTabs(pageContext, tabs, "ms.vss-code-web.pr-tabs", {
            routeStateKey: "_a",
            routeValues: {
                projectName: extensionContext.pullRequest.sourceFriendlyName,
                repositoryName: extensionContext.pullRequest.repositoryName,
                parameters: extensionContext.pullRequest.pullRequestId.toString()
            },
            contextualData: {
                ...extensionContext,
                pullRequestId: extensionContext.pullRequest.pullRequestId,
                repositoryId: extensionContext.pullRequest.repositoryId
            }
        });

        // Track the currently selected tab, item, and iterations
        // We will log a new performance scenario whenever one of these changes
        let currentTabId = prDetailsService.getSelectedTabId().value;
        const currentSelectedItem = navigationService.getSelectedItem().value;
        let currentItemPath = currentSelectedItem ? currentSelectedItem.path : "";
        let currentIterationId = prDetailsService.getSelectedIterationId().value;
        let currentBaseIterationId = prDetailsService.getBaseIterationId().value;

        return (
            <PerformanceScenario area="Repos" scenarioName="pr-details" tti={true}>
                <Surface background={SurfaceBackground.neutral}>
                    <Page
                        className="repos-pr-details-page flex-grow custom-scrollbar scroll-auto-hide"
                        tabProviders={tabs}
                        selectedTabId={prDetailsService.getSelectedTabId()}
                    >
                        <ChangesContext.Provider value={{ filesService, navigationService, viewService: prDetailsService }}>
                            <PullRequestDetailsHeader />
                            <Observer selectedTabId={prDetailsService.getSelectedTabId()}>
                                {({ selectedTabId }: { selectedTabId: string }) => {
                                    const currentTab = tabs.value.filter(t => t.id === selectedTabId)[0] as IPullRequestTab;
                                    const displaySideBySide = currentTab ? currentTab.displaySideBySide : false;
                                    return (
                                        <>
                                            <Observer fullScreen={layoutManager.getFullScreenMode()}>
                                                {(fullScreenProps: { fullScreen: boolean }) =>
                                                    !fullScreenProps.fullScreen ? (
                                                        <TabBar className="repos-pr-details-page-tabbar" onSelectedTabChanged={tabId => prDetailsService.setSelectedTabId(tabId)} />
                                                    ) : null
                                                }
                                            </Observer>
                                            <div
                                                className={css(
                                                    "page-content flex-column flex-noshrink flex-grow rhythm-vertical-16",
                                                    displaySideBySide && "padding-horizontal-0 padding-bottom-0",
                                                    !displaySideBySide && "page-content-top"
                                                )}
                                            >
                                                <ScreenSizeObserver>
                                                    {(screenSizeProps: { screenSize: ScreenSize }) => {
                                                        const typeFilter =
                                                            screenSizeProps.screenSize < ScreenSize.small
                                                                ? (type: string) => !PullRequestDetails.ignoredTypesSmallScreen.has(type)
                                                                : undefined;
                                                        return (
                                                            <ReposMessages
                                                                className={css("flex-noshrink", displaySideBySide && "margin-horizontal-16")}
                                                                typeFilter={typeFilter}
                                                                showPullRequestSuggestions={false}
                                                            />
                                                        );
                                                    }}
                                                </ScreenSizeObserver>
                                                <PerformanceScenarioContext.Consumer>
                                                    {scenarioProps => {
                                                        return (
                                                            <>
                                                                <Observer
                                                                    baseIterationId={prDetailsService.getBaseIterationId()}
                                                                    selectedItem={navigationService.getSelectedItem()}
                                                                    selectedIterationId={prDetailsService.getSelectedIterationId()}
                                                                >
                                                                    {(selectionProps: {
                                                                        baseIterationId: number;
                                                                        selectedItem: IChange;
                                                                        selectedIterationId: number;
                                                                    }) => {
                                                                        const selectedItemPath = selectionProps.selectedItem
                                                                            ? selectionProps.selectedItem.path
                                                                            : "";
                                                                        const newPath = selectedItemPath !== currentItemPath;
                                                                        const newTab = selectedTabId !== currentTabId;
                                                                        const newIteration =
                                                                            selectionProps.selectedIterationId !== currentIterationId ||
                                                                            selectionProps.baseIterationId !== currentBaseIterationId;

                                                                        if (newPath || newTab || newIteration) {
                                                                            const newScenario = scenarioProps.resetScenario()!;
                                                                            newScenario.addProperties({ inPageNavigate: true });
                                                                            newScenario.addProperties({ switchFiles: newPath });
                                                                            newScenario.addProperties({ switchTabs: newTab });
                                                                            newScenario.addProperties({ switchIterations: newIteration });

                                                                            currentItemPath = selectionProps.selectedItem.path;
                                                                            currentTabId = selectedTabId;
                                                                            currentIterationId = selectionProps.selectedIterationId;
                                                                            currentBaseIterationId = selectionProps.baseIterationId;

                                                                            scenarioProps.getScenario()!.addProperties({
                                                                                baseIterationId: selectionProps.baseIterationId,
                                                                                isFolder: selectionProps.selectedItem.isFolder,
                                                                                iterationId: selectionProps.selectedIterationId,
                                                                                tab: selectedTabId
                                                                            });
                                                                        }

                                                                        return null;
                                                                    }}
                                                                </Observer>
                                                            </>
                                                        );
                                                    }}
                                                </PerformanceScenarioContext.Consumer>
                                                <PullRequestDetailsTabContent />
                                            </div>
                                        </>
                                    );
                                }}
                            </Observer>
                        </ChangesContext.Provider>
                    </Page>
                </Surface>
            </PerformanceScenario>
        );
    }

    public componentDidMount(): void {
        super.componentDidMount();
        let prDetailsService: IPullRequestViewService;

        try {
            prDetailsService = getPullRequestViewService(this.context.pageContext);
        } catch(ex) {
            // PR details data does not exist.
            return;
        }
         
        prDetailsService.loadAdornmentProviders();

        // Kick off the live update monitoring after a short delay.
        // Wait a second while things are loading to not eat into the page loading experience.
        this.timerManagement.setTimeout(() => {
            prDetailsService.startLiveUpdates();
        }, 1000);
    }

    public componentWillUnmount() {
        super.componentWillUnmount();

        this.timerManagement.dispose();
    }
}

const PullRequestDetailsTabContent = (): JSX.Element => {
    let pendingWork: IPerformanceScenarioPendingWork | undefined;

    React.useEffect(() => {
        // Register and complete pending work before rendering a tab through
        // its component did mount/update.
        if (pendingWork) {
            pendingWork.complete();
        }
    });

    return (
        <PerformanceScenarioContext.Consumer>
            {scenarioProps => {
                pendingWork = scenarioProps.registerPendingWork("pr-render-tab");
                return <TabContent />;
            }}
        </PerformanceScenarioContext.Consumer>
    );
};

Components.add("repos-pr-details-component", PullRequestDetails);
